import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const faqCategories = [
  {
    id: 'choosing',
    title: 'How to choose the right spice for cooking',
    questions: [
      {
        id: 'q1',
        question: 'Which chilli powder is best for biriyani?',
        answer: (
          <>
            <Link to="/products/kashmiri-chilli-powder" className="text-[#8B2820] hover:underline font-semibold">Kashmiri chilli powder</Link> is ideal for biriyani because it gives a rich red color without too much heat. It adds vibrant color while keeping the spice level mild, perfect for dishes where appearance matters as much as taste.
          </>
        )
      },
      {
        id: 'q2',
        question: 'Which chilli powder is best for spicy curries?',
        answer: (
          <>
            <Link to="/products/chilli-powder" className="text-[#8B2820] hover:underline font-semibold">Regular red chilli powder</Link> or Guntur chilli is better for strong spice and heat. These varieties deliver authentic pungency and are perfect for traditional spicy curries and dishes that demand bold heat.
          </>
        )
      },
      {
        id: 'q3',
        question: 'What is the difference between Kashmiri chilli and normal chilli?',
        answer: (
          <>
            Kashmiri chilli provides color with mild heat, while normal chilli is more pungent and spicy. Learn more about different chilli varieties on our <Link to="/spices-guide" className="text-[#8B2820] hover:underline font-semibold">Spices Guide</Link> page where we explain heat levels, color profiles, and best uses for each type.
          </>
        )
      }
    ]
  },
  {
    id: 'quality',
    title: 'How to identify good quality spices',
    questions: [
      {
        id: 'q4',
        question: 'How to check turmeric purity at home?',
        answer: (
          <>
            Add <Link to="/products/turmeric-powder" className="text-[#8B2820] hover:underline font-semibold">turmeric powder</Link> to water. If artificial color spreads quickly and stains the container, it may be adulterated. Pure turmeric settles slowly and doesn't leave bright yellow stains on your fingers. Visit our <Link to="/spices-guide" className="text-[#8B2820] hover:underline font-semibold">Spices Guide</Link> for more quality testing tips.
          </>
        )
      },
      {
        id: 'q5',
        question: 'How to identify pure chilli powder?',
        answer: 'Check for natural aroma, consistent color, and avoid powders that look artificially bright. Pure chilli powder should have a natural earthy smell, feel slightly coarse (not too fine), and maintain consistent color throughout without any artificial additives. At Agniya Spices, we ensure no artificial colors are added to any of our products.'
      },
      {
        id: 'q6',
        question: 'Why does spice color matter?',
        answer: (
          <>
            Color indicates the type and quality of spice. For example, <Link to="/products/kashmiri-chilli-powder" className="text-[#8B2820] hover:underline font-semibold">Kashmiri chilli</Link> is bright red but mild, while darker red chillies tend to be more pungent. Natural color also ensures no artificial dyes have been added, which is important for health and authentic taste.
          </>
        )
      }
    ]
  },
  {
    id: 'usage',
    title: 'How to use spices in cooking',
    questions: [
      {
        id: 'q7',
        question: 'How much chilli powder should I use in cooking?',
        answer: 'Start with small quantities (½ to 1 teaspoon for 4 servings) and adjust based on taste and dish type. Different chillies have different heat levels, so it\'s always better to start conservatively. You can always add more, but you can\'t reduce spice once added. For biriyani and color-focused dishes, you can use more Kashmiri chilli without increasing heat.'
      },
      {
        id: 'q8',
        question: 'Which spices are commonly used in Indian cooking?',
        answer: (
          <>
            Chilli, <Link to="/products/turmeric-powder" className="text-[#8B2820] hover:underline font-semibold">turmeric</Link>, <Link to="/products/coriander-powder" className="text-[#8B2820] hover:underline font-semibold">coriander</Link>, black pepper, cumin, and other regional spices are commonly used. Each spice plays a specific role – turmeric for color and health benefits, coriander for base flavor, chilli for heat, and cumin for aroma. Explore our full range to understand each spice better.
          </>
        )
      }
    ]
  },
  {
    id: 'storage',
    title: 'How to store spices properly',
    questions: [
      {
        id: 'q9',
        question: 'How to store spices to keep them fresh?',
        answer: 'Store spices in airtight containers away from heat, light, and moisture. Keep them in a cool, dry place like a cupboard – not near the stove or window. Glass or metal containers work better than plastic. Always use a dry spoon when taking out spices to prevent moisture from entering the container.'
      },
      {
        id: 'q10',
        question: 'How long do spice powders stay fresh?',
        answer: 'Spice powders stay fresh for about 6 months when stored properly in airtight containers. However, they don\'t spoil after this time – they just start losing aroma and potency. For best flavor, buy quantities you can use within 3-6 months. Whole spices last longer (1-2 years) than ground powders.'
      }
    ]
  },
  {
    id: 'bulk',
    title: 'Buying spices for home and business',
    questions: [
      {
        id: 'q11',
        question: 'Can I buy spices in bulk from Agniya?',
        answer: (
          <>
            Yes, Agniya offers bulk options for hotels, caterers, and businesses. We provide packaging options from 1kg to 10kg or more for commercial use. Contact us via <a href="https://wa.me/918086075558?text=Hi,%20I%20need%20bulk%20spices%20for%20my%20business" target="_blank" rel="noopener noreferrer" className="text-[#8B2820] hover:underline font-semibold">WhatsApp</a> or our <Link to="/contact" className="text-[#8B2820] hover:underline font-semibold">contact form</Link> to discuss bulk requirements and pricing.
          </>
        )
      },
      {
        id: 'q12',
        question: 'How to choose spices for hotel cooking?',
        answer: 'Focus on consistency, aroma, color, and supplier reliability. For commercial kitchens, consistent quality across batches is crucial. Look for suppliers who can provide regular supply, maintain quality standards, and offer proper packaging. Agniya Spices is trusted by restaurants and caterers across Kerala for our consistent quality and reliable supply.'
      }
    ]
  }
];

const FAQPage = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi, help me choose the right spice");
    window.open(`https://wa.me/918086075558?text=${message}`, '_blank');
  };

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": typeof q.answer === 'string' ? q.answer : q.question.includes('biriyani') ? 'Kashmiri chilli powder is ideal for biriyani because it gives a rich red color without too much heat.' : 'Check our website for detailed answer.'
        }
      }))
    )
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Spices FAQ | How to Choose, Store & Use Spices | Agniya Spices</title>
        <meta 
          name="description" 
          content="Find answers to common questions about spices including how to choose chilli powder, check turmeric purity, store spices, and more." 
        />
        <meta 
          name="keywords" 
          content="spices FAQ, how to choose chilli powder, turmeric purity test, store spices, Indian spices questions, Kashmir chilli vs regular chilli, spice storage tips" 
        />
        <link rel="canonical" href="https://agniyaspices.com/faq" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Spices FAQ - Everything About Choosing & Using Spices" />
        <meta property="og:description" content="Get answers to common questions about spices - choosing, storing, and using them for Indian cooking." />
        <meta property="og:url" content="https://agniyaspices.com/faq" />
        <meta property="og:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        <meta property="og:site_name" content="Agniya Spices" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Spices FAQ - Agniya Spices" />
        <meta name="twitter:description" content="Answers to common questions about choosing, storing, and using Indian spices." />
        <meta name="twitter:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://agniyaspices.com" },
              { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://agniyaspices.com/faq" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">
              AGNIYA SPICES
            </div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#8B2820] font-semibold transition-colors">FAQ</Link>
            <Link to="/#quality" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Quality</Link>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center mb-6">
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/20">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Spices FAQ – Everything You Need to Know
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed">
              Get answers to common questions about choosing, using, and storing spices for Indian cooking.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          {faqCategories.map((category, categoryIndex) => (
            <div key={category.id} className={categoryIndex > 0 ? 'mt-16' : ''}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#8B2820] mb-8">
                {category.title}
              </h2>
              
              <Accordion type="single" collapsible className="w-full space-y-4">
                {category.questions.map((item) => (
                  <AccordionItem 
                    key={item.id} 
                    value={item.id}
                    className="border border-[#8B2820]/20 rounded-lg px-6 bg-[#f7f5f2] hover:bg-white transition-colors"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold text-[#4a3a33] hover:text-[#8B2820] py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4a3a33] leading-relaxed pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Still Confused About Which Spice to Choose?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Our team is here to help you find the perfect spice for your cooking needs. Get personalized recommendations.
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-white text-[#8B2820] hover:bg-white/90 px-12 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Ask on WhatsApp
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
